// request.js
import axios from "axios";
import router from "@/router";
import store from "@/store";
import { MessageBox, Message, Loading } from "element-ui";
import i18n from "@/lang"; // 👈 导入 i18n 实例

// 创建axios实例
const isDev = process.env.NODE_ENV === 'development'
const service = axios.create({
  baseURL: isDev ? '/api' : '',
  timeout: 0,
});

// 请求队列
const pendingRequest = new Map();

// 添加标志位防止重复弹窗
let isShowingLogoutModal = false;

// 遮罩层相关变量
let loadingInstance = null;
let requestCount = 0;

/**
 * 显示遮罩层
 * @param {string} text 遮罩层显示的文字
 */
function showLoading(text = i18n.t('Request.加载中')) {
  if (requestCount === 0) {
    loadingInstance = Loading.service({
      lock: true,
      fullscreen: true,
      spinner: 'el-icon-loading',
      text: text,
      background: "rgba(0, 0, 0, 0.7)",
    });
  }
  requestCount++;
}

/**
 * 隐藏遮罩层
 */
function hideLoading() {
  if (requestCount > 0) {
    requestCount--;
  }
  if (requestCount === 0 && loadingInstance) {
    loadingInstance.close();
    loadingInstance = null;
  }
}

/**
 * 重置遮罩层状态（用于异常情况）
 */
export function resetLoading() {
  requestCount = 0;
  if (loadingInstance) {
    loadingInstance.close();
    loadingInstance = null;
  }
}

/**
 * 将对象序列化为查询字符串（替代qs.stringify）
 * @param {Object} obj 需要序列化的对象
 * @returns {string} 序列化后的字符串
 */
function stringifyParams(obj) {
  if (!obj || typeof obj !== 'object') return '';

  const pairs = [];
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          // 嵌套对象转换为JSON字符串
          pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`);
        } else {
          pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      }
    }
  }
  return pairs.join('&');
}

// 生成请求的唯一标识
function generateRequestKey(config) {
  const { method, url, params, data } = config;

  // 序列化params和data
  const paramsStr = params ? stringifyParams(params) : '';
  const dataStr = data ? stringifyParams(data) : '';

  return [method, url, paramsStr, dataStr].join('&');
}

// 添加请求到队列
function addPendingRequest(config) {
  const requestKey = generateRequestKey(config);
  config.cancelToken =
    config.cancelToken ||
    new axios.CancelToken((cancel) => {
      if (!pendingRequest.has(requestKey)) {
        pendingRequest.set(requestKey, cancel);
      }
    });
}

// 移除队列中的请求
function removePendingRequest(config) {
  const requestKey = generateRequestKey(config);
  if (pendingRequest.has(requestKey)) {
    const cancel = pendingRequest.get(requestKey);
    cancel(requestKey);
    pendingRequest.delete(requestKey);
  }
}

// 清空所有pending状态的请求
export function clearPendingRequest() {
  for (const [requestKey, cancel] of pendingRequest) {
    cancel(requestKey);
  }
  pendingRequest.clear();
}

// 处理401错误的函数
async function handleUnauthorizedError() {
  if (isShowingLogoutModal) return;

  isShowingLogoutModal = true;

  // 401错误时强制关闭遮罩层
  resetLoading();

  try {
    await MessageBox.confirm(i18n.t('Request.您已退出登录'), i18n.t('Request.确认退出'), {
      confirmButtonText: i18n.t('Request.重新登录'),
      showCancelButton: false,
      type: "warning",
    });

    // 用户点击确认退出
    await store.dispatch("user/logout");
    router.push("/login");
  } catch (_) {
    isShowingLogoutModal = false;
  } finally {
    isShowingLogoutModal = false;
  }
}

// 判断是否需要显示遮罩层的请求
function shouldShowLoading(config) {
  // 如果配置中明确指定了不显示遮罩层，则返回false
  if (config.data && config.data.Loading === true) {
    return true;
  }
  // 默认显示遮罩层
  return false;
}

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    removePendingRequest(config);
    addPendingRequest(config);

    // 显示遮罩层
    if (shouldShowLoading(config)) {
      showLoading(config.loadingText || i18n.t('Request.加载中'));
    }

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token) {
      config.headers.Token = token;
    }

    const lang = localStorage.getItem("languageInfo") ? JSON.parse(localStorage.getItem("languageInfo")).lang : 'en'
    config.headers['X-Lang'] = lang;

    if (config.method === "get" && config.params) {
      let url = config.url + "?";
      for (const key in config.params) {
        if (config.params[key] !== undefined && config.params[key] !== null) {
          url += `${key}=${encodeURIComponent(config.params[key])}&`;
        }
      }
      config.url = url.slice(0, -1);
      config.params = {};
    }
    return config;
  },
  (error) => {
    // 请求错误时也要隐藏遮罩层
    hideLoading();
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    removePendingRequest(response.config);
    // 隐藏遮罩层
    hideLoading();

    const res = response.data;

    if (res.code !== 1) {
      const path = router.currentRoute.path;

      if (res.code === 401 && path !== "/login") {
        // 统一在这里处理401错误
        handleUnauthorizedError();
        return Promise.reject(new Error(res.msg || i18n.t('Request.错误')));
      }

      Message({
        message: res.msg || i18n.t('Request.错误'),
        type: "error",
        duration: 2 * 1000,
      });
      return Promise.reject(new Error(res.msg || i18n.t('Request.错误')));
    } else {
      return res;
    }
  },
  (error) => {
    if (error.config) {
      removePendingRequest(error.config);
    }
    // 隐藏遮罩层
    hideLoading();

    // 处理网络错误或其他错误
    if (error.response) {
      // 处理401错误
      if (error.response.status === 401 && router.currentRoute.path !== "/login") {
        handleUnauthorizedError();
      } else {
        Message({
          message: error.response.data?.msg || error.message,
          type: "error",
          duration: 2 * 1000,
        });
      }
    } else if (error.message !== 'canceled' && !error.message?.includes('canceled')) {
      // 过滤掉取消请求的错误提示
      Message({
        message: error.message || i18n.t('Request.网络错误'),
        type: "error",
        duration: 2 * 1000,
      });
    }

    return Promise.reject(error);
  }
);

/**
 * 封装请求方法
 * @param {Object} config 请求配置
 * @returns {Promise} Promise对象
 */
function request(config) {
  return new Promise((resolve, reject) => {
    service(config).then((res) => {
      resolve(res);
    }).catch((err) => {
      reject(err);
    });
  });
}

/**
 * GET请求
 * @param {string} url 请求地址
 * @param {Object} params 请求参数
 * @param {Object} config 额外配置
 * @param {boolean} config.noLoading 是否不显示遮罩层
 * @param {string} config.loadingText 遮罩层显示文字
 * @returns {Promise} Promise对象
 */
export function get(url, params = {}, config = {}) {
  return request({
    url,
    method: "GET",
    params,
    ...config,
  });
}

/**
 * POST请求
 * @param {string} url 请求地址
 * @param {Object} data 请求体数据
 * @param {Object} config 额外配置
 * @param {boolean} config.noLoading 是否不显示遮罩层
 * @param {string} config.loadingText 遮罩层显示文字
 * @returns {Promise} Promise对象
 */
export function post(url, data = {}, config = {}) {
  return request({
    url,
    method: "POST",
    data,
    ...config,
  });
}

/**
 * PUT请求
 * @param {string} url 请求地址
 * @param {Object} data 请求体数据
 * @param {Object} config 额外配置
 * @param {boolean} config.noLoading 是否不显示遮罩层
 * @param {string} config.loadingText 遮罩层显示文字
 * @returns {Promise} Promise对象
 */
export function put(url, data = {}, config = {}) {
  return request({
    url,
    method: "PUT",
    data,
    ...config,
  });
}

/**
 * DELETE请求
 * @param {string} url 请求地址
 * @param {Object} params 请求参数
 * @param {Object} config 额外配置
 * @param {boolean} config.noLoading 是否不显示遮罩层
 * @param {string} config.loadingText 遮罩层显示文字
 * @returns {Promise} Promise对象
 */
export function del(url, params = {}, config = {}) {
  return request({
    url,
    method: "DELETE",
    params,
    ...config,
  });
}

/**
 * 上传文件请求
 * @param {string} url 请求地址
 * @param {FormData} formData 表单数据
 * @param {Object} config 额外配置
 * @param {boolean} config.noLoading 是否不显示遮罩层
 * @param {string} config.loadingText 遮罩层显示文字
 * @returns {Promise} Promise对象
 */
export function upload(url, formData, config = {}) {
  return request({
    url,
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
    ...config,
  });
}

/**
 * 下载文件请求
 * @param {string} url 请求地址
 * @param {Object} params 请求参数
 * @param {string} filename 文件名
 * @param {Object} config 额外配置
 * @param {boolean} config.noLoading 是否不显示遮罩层
 * @param {string} config.loadingText 遮罩层显示文字
 * @returns {Promise} Promise对象
 */
export function download(url, params = {}, filename = "file", config = {}) {
  return new Promise((resolve, reject) => {
    // 下载请求单独处理遮罩层显示
    if (shouldShowLoading(config)) {
      showLoading(config.loadingText || i18n.t('Request.下载中'));
    }

    service({
      url,
      method: "GET",
      params,
      responseType: "blob",
      ...config,
    }).then((response) => {
      // 下载完成隐藏遮罩层
      hideLoading();

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      resolve(response);
    }).catch((err) => {
      hideLoading();
      reject(err);
    });
  });
}

export default {
  request,
  get,
  post,
  put,
  delete: del,
  upload,
  download,
  clearPendingRequest,
  resetLoading,
};